import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  useDisclosure,
  FormErrorMessage,
  Text,
  Input,
  Flex,
  Box,
  Textarea,
} from '@chakra-ui/react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { AddressInfo, PropertyFormValues } from 'types/property.type'
import { useEffect, useState } from 'react'
import React from 'react'
import { Alert, AlertIcon, AlertDescription } from '@chakra-ui/react'
import Select from 'components/form/react-select'
import ReactSelect from 'components/form/react-select'
import { useGetAddressVerification } from 'api/pc-projects'
import { useTranslation } from 'react-i18next'
import { AddressVerificationModal } from './address-verification-modal'
import { fieldsLimitNumber, usePropertyInformationNextDisabled } from './hooks'
import { NEW_PROPERTY } from 'features/property/property.i18n'
import { SelectOption } from 'types/transaction.type'
import { NEW_PROJECT } from 'features/vendor/projects/projects.i18n'

export const AddPropertyAddressInfo: React.FC<{
  isLoading: boolean
  isDuplicateAddress: boolean
  setIsDuplicateAddress: (isDuplicate: boolean) => void
  setNextTab: () => void
  onClose: () => void
  clientSelectOptions: SelectOption[]
  stateSelectOptions: SelectOption[]
  marketSelectOptions: SelectOption[]
  isEdit?: boolean
}> = props => {
  const { t } = useTranslation()

  const {
    isOpen: isAddressVerficationModalOpen,
    onOpen: onAddressVerificationModalOpen,
    onClose: onAddressVerificationModalClose,
  } = useDisclosure()
  const { isDuplicateAddress, setIsDuplicateAddress, clientSelectOptions, stateSelectOptions, marketSelectOptions } =
    props
  const [addressInfo, setAddressInfo] = useState<AddressInfo>({
    address: '',
    city: '',
    state: '',
    zipCode: '',
  })
  const [defaultAddressInfo, setDefaultAddressInfo] = useState<any>(null)

  const [check, setCheck] = useState(false)
  const [existProperty] = useState([{ id: 0, status: '' }])

  // API calls

  const { data: isAddressVerified, refetch, isLoading } = useGetAddressVerification(addressInfo)

  const {
    register,
    formState: { errors },
    control,
    setValue,
    setError,
    clearErrors,
  } = useFormContext<PropertyFormValues>()

  const isNextButtonDisabled = usePropertyInformationNextDisabled(control as any, isDuplicateAddress)

  //const addressShouldBeVerified = useAddressShouldBeVerified(control as any)

  // Continue unverified Check
  const handleCheck = () => {
    setCheck(!check)
    setIsDuplicateAddress(false)
    setValue('acknowledgeCheck', true)
  }

  // Get all values of Address Info
  const watchAddress = useWatch({ name: 'streetAddress', control })
  const watchCity = useWatch({ name: 'city', control })
  const watchState = useWatch({ name: 'state', control })
  const watchZipCode = useWatch({ name: 'zipCode', control })

  const isAddressEmpty = watchAddress?.trim() === ''
  const isCityEmpty = watchCity?.trim() === ''

  // Set all values of Address Info
  useEffect(() => {
    setAddressInfo({
      address: watchAddress || '',
      city: watchCity || '',
      state: watchState?.label || '',
      zipCode: watchZipCode || '',
    })
  }, [watchAddress, watchCity, watchState, watchZipCode])

  // On Street Address change, set values of City, State and Zip
  /*const setAddressValues = option => {
    const property = option?.property
    const market = markets.find(m => m?.id === property?.marketId)

    const state = states.find(s => s?.code === property?.state)

    setValue('streetAddress', property?.streetAddress || option.label)
    setValue('city', property?.city)
    setValue('zipCode', property?.zipCode)
    setValue('propertyId', property?.id)
    setValue('property', property)
    setValue('newMarket', { label: market?.metropolitanServiceArea, value: market?.id })
    setValue('state', { label: state?.name, value: state?.code })

    // Check for duplicate address
    const duplicatedInProjects =
      projects?.filter(
        p =>
          p.propertyId === property?.id &&
          [STATUS.New, STATUS.Active, STATUS.Punch, STATUS.Closed].includes(p.projectStatus?.toLowerCase() as STATUS),
      ) || []
    setIsDuplicateAddress(false)
    setValue('acknowledgeCheck', false)

    if (duplicatedInProjects?.length > 0) {
      setIsDuplicateAddress(true)
      setExistProperty(duplicatedInProjects.map(p => ({ id: p.id as number, status: p.projectStatus as string })))
    }
  }*/

  const propertyVerified = React.useMemo(() => {
    if (defaultAddressInfo?.address !== addressInfo.address) return false
    if (defaultAddressInfo?.city !== addressInfo.city) return false

    if (defaultAddressInfo?.state !== addressInfo.state) return false
    if (defaultAddressInfo?.zipCode !== addressInfo.zipCode) return false

    return true
  }, [addressInfo, defaultAddressInfo])

  useEffect(() => {
    if (watchState?.label && defaultAddressInfo === null && addressInfo.state !== '') {
      setDefaultAddressInfo(addressInfo)
    }
  }, [watchState, addressInfo])

  const refSubmitButtom = React.useRef<HTMLButtonElement>(null)

  return (
    <>
      <Flex flexDir="column">
        <Box px="6" minH="300px">
          {isDuplicateAddress && (
            <Alert status="info" mb={5} bg="#EBF8FF" rounded={6} width="75%">
              <AlertIcon />
              <AlertDescription>
                <Text color="red" fontSize="sm">
                  {existProperty &&
                    existProperty?.map(p => 'Property ID ' + p.id + ' using this address already exist.')}
                </Text>
              </AlertDescription>
              <Divider orientation="vertical" h={6} marginLeft={6} />
              <Checkbox
                name={`acknowledgeCheck`}
                type="checkbox"
                marginTop={1}
                marginLeft={6}
                color="#4E87F8"
                onChange={handleCheck}
              >
                {t(`${NEW_PROPERTY}.acknowledged`)}
              </Checkbox>
            </Alert>
          )}
          <Grid templateColumns="repeat(2, 400px)" gap={'1rem 1rem'} pb="2">
            <GridItem w="455px" zIndex="999">
              <FormControl isInvalid={!!errors?.streetAddress}>
                <FormLabel size="md">{t(`${NEW_PROPERTY}.address`)}</FormLabel>
                <Controller
                  control={control}
                  name={`streetAddress`}
                  rules={{ required: 'This is required field' }}
                  render={({ field, fieldState }) => (
                    <>
                      <Input
                        id="streetAddress"
                        variant="required-field"
                        autoComplete="off"
                        autoCorrect="off"
                        value={field.value}
                        onChange={e => {
                          field.onChange(e)
                          if ((e.target as HTMLInputElement).value?.length > fieldsLimitNumber) {
                            setError(field.name, {
                              type: 'custom',
                              message: t(`${NEW_PROJECT}.fieldLimitReached`),
                            })
                          } else {
                            clearErrors(field.name)
                          }
                        }}
                      />
                    </>
                  )}
                />
                <FormErrorMessage>{errors?.streetAddress && errors?.streetAddress?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem w="53.6%" ml="17%">
              <FormControl isInvalid={!!errors?.unitNo}>
                <FormLabel size="md" htmlFor="unitNo">
                  {t(`${NEW_PROPERTY}.unitNumber`)}
                </FormLabel>
                <Input
                  id="unitNo"
                  {...register('unitNo')}
                  maxLength={255}
                  onChange={e => {
                    setValue('unitNo', (e.target as HTMLInputElement).value)
                    if ((e.target as HTMLInputElement).value?.length > fieldsLimitNumber) {
                      setError('unitNo', {
                        type: 'custom',
                        message: t(`${NEW_PROJECT}.fieldLimitReached`),
                      })
                    } else {
                      clearErrors('unitNo')
                    }
                  }}
                />
                <FormErrorMessage>{errors?.unitNo && errors?.unitNo?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>

          <Grid templateColumns="repeat(4, 215px)" gap={'1rem 1.7rem'} pb="2" mt="25px">
            <GridItem>
              <FormControl isInvalid={!!errors?.city}>
                <FormLabel size="md" htmlFor="city">
                  {t(`${NEW_PROPERTY}.city`)}
                </FormLabel>
                <Input
                  id="city"
                  variant="required-field"
                  {...register('city', {
                    required: true,
                    onChange: e => {
                      if ((e.target as HTMLInputElement).value?.length > fieldsLimitNumber) {
                        setError('city', {
                          type: 'custom',
                          message: t(`${NEW_PROJECT}.fieldLimitReached`),
                        })
                      } else {
                        clearErrors('city')
                      }
                      const city = e.target.value?.replace(/[^a-zA-Z\s]/g, '')
                      setValue('city', city)
                      setAddressInfo({ ...addressInfo, city })
                    },
                  })}
                />
                <FormErrorMessage>{errors?.city && errors?.city?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel size="md">{t(`${NEW_PROPERTY}.state`)}</FormLabel>
                <Controller
                  control={control}
                  name={`state`}
                  rules={{ required: 'This is required field' }}
                  render={({ field, fieldState }) => (
                    <>
                      <Select
                        {...field}
                        options={stateSelectOptions}
                        size="md"
                        value={field.value}
                        selectProps={{ isBorderLeft: true, menuHeight: '215px' }}
                        onChange={option => {
                          setAddressInfo({ ...addressInfo, state: option?.value })
                          field.onChange(option)
                        }}
                      />
                      <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                    </>
                  )}
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isInvalid={!!errors?.zipCode}>
                <FormLabel htmlFor="zipCode" size="md">
                  {t(`${NEW_PROPERTY}.zipCode`)}
                </FormLabel>
                <Input
                  id="zipCode"
                  variant="required-field"
                  {...register('zipCode', {
                    required: true,
                    onChange: e => {
                      setAddressInfo({ ...addressInfo, zipCode: e.target.value })
                    },
                  })}
                  type="number"
                />
                <FormErrorMessage>{errors?.zipCode && errors?.zipCode?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>
          <Grid templateColumns="repeat(4, 215px)" gap={'1rem 1.7rem'} py="2" mt="25px">
            <GridItem>
              <FormControl>
                <FormLabel size="md" htmlFor="client">
                  {t(`${NEW_PROPERTY}.client`)}
                </FormLabel>
                <Controller
                  control={control}
                  name={`client`}
                  rules={{ required: 'This is required field' }}
                  render={({ field, fieldState }) => (
                    <>
                      <ReactSelect
                        id="client"
                        options={clientSelectOptions}
                        value={field.value}
                        onChange={e => field.onChange(e)}
                        selectProps={{ isBorderLeft: true, menuHeight: '215px' }}
                      />
                      <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                    </>
                  )}
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl left="0px">
                <FormLabel htmlFor="market" size="md">
                  {t(`${NEW_PROPERTY}.market`)}
                </FormLabel>
                <Controller
                  control={control}
                  name={`market`}
                  rules={{ required: 'This is required field' }}
                  render={({ field, fieldState }) => (
                    <>
                      <Select
                        {...field}
                        options={marketSelectOptions}
                        size="md"
                        value={field.value}
                        selectProps={{ isBorderLeft: true, menuHeight: '120px' }}
                        onChange={option => {
                          field.onChange(option)
                        }}
                      />
                      <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                    </>
                  )}
                />
              </FormControl>
            </GridItem>
          </Grid>

          <Grid templateColumns="repeat(1, 58%)" gap={'1rem 1rem'} pb="2" mb="25px" mt="25px">
            <GridItem w="100%">
              <FormControl isInvalid={!!errors?.propertyNotes}>
                <FormLabel size="md">{t(`${NEW_PROPERTY}.notes`)}:</FormLabel>
                <Controller
                  control={control}
                  name={`propertyNotes`}
                  render={({ field, fieldState }) => (
                    <>
                      <Textarea
                        id="propertyNotes"
                        onChange={e => {
                          field.onChange(e)
                          if ((e?.target as any).value?.length > 65535) {
                            setError(field.name, {
                              type: 'custom',
                              message: t(`${NEW_PROJECT}.fieldLimitReached`),
                            })
                          } else {
                            clearErrors(field.name)
                          }
                        }}
                        autoComplete="off"
                        autoCorrect="off"
                        value={field.value as string}
                        placeholder="Add Notes (if any)"
                        resize="none"
                        fontSize="12px"
                      />
                    </>
                  )}
                />
                <FormErrorMessage>{errors?.propertyNotes && errors?.propertyNotes?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>
        </Box>

        <Flex display="flex" justifyContent="end" borderTop="1px solid #E2E8F0" pt="5" px="6">
          <Button onClick={props.onClose} variant="outline" size="md" colorScheme="brand">
            {t('cancel')}
          </Button>
          <Button
            mr="-27px"
            colorScheme="brand"
            ml="2"
            size="md"
            disabled={isNextButtonDisabled || isAddressEmpty || isCityEmpty}
            onClick={() => {
              if (!propertyVerified) {
                refetch()
                onAddressVerificationModalOpen()
              } else {
                if (!props.isEdit) props.setNextTab()
              }
            }}
            type={props.isEdit && propertyVerified ? 'submit' : 'button'}
          >
            {props.isEdit ? t('save') : t('next')}
          </Button>
          <button hidden={true} ref={refSubmitButtom} type={'submit'} />
        </Flex>
      </Flex>

      <AddressVerificationModal
        isOpen={isAddressVerficationModalOpen}
        onClose={onAddressVerificationModalClose}
        onSave={props.setNextTab}
        isAddressVerified={isAddressVerified}
        isLoading={isLoading}
        isEdit={props.isEdit}
        refSubmitButtom={refSubmitButtom}
      />
    </>
  )
}
